import { patchQuery } from '../../services/restartReload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    CreateRetroActionItemDto,
    RetroActionItemDto,
    RetroBoardDto,
    RetroCardDto,
    RetroCardVotedEvent,
    RetroCommentDto,
    RetroDto,
    RetroPhase,
    RetroTimerDto,
    RetroTimerState,
    UpdateRetroActionItemDto,
    UpdateRetroCardDto,
    UpdateRetroDto,
} from '../../../../shared/types/retro';
import { api, shouldRetry } from '../../services/api';
import {
    addComment,
    applyVote,
    changesWhatCardsShow,
    fromDto,
    moveCardLocally,
    pendingCard,
    removeCard,
    setActions,
    setRetro,
    setTimer,
    upsertCard,
    voteLocally,
    type RetroBoard,
} from './boardCache';
import type { NewSpaceAccessDto } from '../../../../shared/types/access';
import { platformKeys } from '../../services/platform';

export const retroKeys = {
    board: (shareId: string) => ['retro', shareId] as const,
};

const base = (shareId: string) => `/api/retros/${encodeURIComponent(shareId)}`;

export async function createRetro(title: string, access: NewSpaceAccessDto): Promise<RetroDto> {
    return (await api.post<RetroDto>('/api/retros', { title, ...access })).data;
}

/** A new retro with this one's columns, phase options and vote limit, under a new name and access. */
/** Bring an archived retro back (from Home's Archived list). */
export async function restoreRetro(shareId: string): Promise<void> {
    await api.post(`${base(shareId)}/archive`, { archived: false });
}

export async function duplicateRetro(shareId: string, title: string, access: NewSpaceAccessDto): Promise<RetroDto> {
    return (await api.post<RetroDto>(`${base(shareId)}/duplicate`, { title, ...access })).data;
}

export function useRetroBoard(shareId: string | undefined, enabled: boolean) {
    return useQuery({
        queryKey: retroKeys.board(shareId ?? ''),
        queryFn: async () => fromDto((await api.get<RetroBoardDto>(base(shareId!))).data),
        enabled: !!shareId && enabled,
        retry: shouldRetry,
        staleTime: 60_000,
    });
}

/** Update the cached board, if loaded (a reload already on its way predates this change, so it starts again). */
export function useBoardCache(shareId: string) {
    const qc = useQueryClient();
    return <T,>(fn: (board: RetroBoard, data: T) => RetroBoard, data: T) =>
        patchQuery<RetroBoard>(qc, retroKeys.board(shareId), old => fn(old, data));
}

/** All retro writes. Each applies its result to the cache; live events from others arrive separately. */
export function useRetroActions(shareId: string) {
    const qc = useQueryClient();
    const key = retroKeys.board(shareId);
    const apply = useBoardCache(shareId);
    const snapshot = () => qc.getQueryData<RetroBoard>(key);
    const restore = (prev: RetroBoard | undefined) => prev && qc.setQueryData(key, prev);

    const join = useMutation({
        mutationFn: async () => (await api.post<RetroBoardDto>(`${base(shareId)}/join`)).data,
        onSuccess: board => qc.setQueryData(key, fromDto(board)),
    });

    const update = useMutation({
        mutationFn: async (dto: UpdateRetroDto) => (await api.put<RetroDto>(base(shareId), dto)).data,
        onSuccess: retro => {
            const before = snapshot()?.retro;
            apply(setRetro, retro);
            if (changesWhatCardsShow(before, retro)) qc.invalidateQueries({ queryKey: key });
        },
    });

    const start = useMutation({
        mutationFn: async () => (await api.post<RetroDto>(`${base(shareId)}/start`)).data,
        onSuccess: retro => apply(setRetro, retro),
    });

    const phase = useMutation({
        mutationFn: async (next: RetroPhase) => (await api.post<RetroDto>(`${base(shareId)}/phase`, { phase: next })).data,
        // The new phase's options may turn cards over, or show votes or names: then the board reloads
        onSuccess: retro => {
            const before = snapshot()?.retro;
            apply(setRetro, retro);
            if (changesWhatCardsShow(before, retro)) qc.invalidateQueries({ queryKey: key });
        },
    });

    const conclude = useMutation({
        mutationFn: async () => (await api.post<RetroDto>(`${base(shareId)}/conclude`)).data,
        onSuccess: retro => apply(setRetro, retro),
    });

    const archive = useMutation({
        mutationFn: async (archived: boolean) => (await api.post<RetroDto>(`${base(shareId)}/archive`, { archived })).data,
        onSuccess: retro => {
            apply(setRetro, retro);
            qc.invalidateQueries({ queryKey: platformKeys.all });
        },
    });

    const remove = useMutation({
        mutationFn: async () => api.delete(base(shareId)),
        onSuccess: () => qc.invalidateQueries({ queryKey: platformKeys.all }),
    });

    const timer = useMutation({
        mutationFn: async (dto: RetroTimerDto) => (await api.post<RetroTimerState>(`${base(shareId)}/timer`, dto)).data,
        onSuccess: state => apply(setTimer, state),
    });

    const addCard = useMutation({
        mutationFn: async (dto: { columnId: string; text: string }) => (await api.post<RetroCardDto>(`${base(shareId)}/cards`, dto)).data,
        // The card shows at once (dimmed) so a slow connection never looks like a lost card
        onMutate: ({ columnId, text }) => {
            const prev = snapshot();
            if (!prev) return {};
            const card = pendingCard(prev, columnId, text);
            apply(upsertCard, card);
            return { pendingId: card._id };
        },
        onSuccess: (card, _dto, ctx) => {
            if (ctx?.pendingId) apply(removeCard, { cardId: ctx.pendingId });
            apply(upsertCard, card);
        },
        onError: (_err, _dto, ctx) => {
            if (ctx?.pendingId) apply(removeCard, { cardId: ctx.pendingId });
        },
    });

    const updateCard = useMutation({
        mutationFn: async ({ cardId, dto }: { cardId: string; dto: UpdateRetroCardDto }) =>
            (await api.put<RetroCardDto>(`${base(shareId)}/cards/${cardId}`, dto)).data,
        onMutate: ({ cardId, dto }) => {
            const prev = snapshot();
            if (dto.columnId !== undefined || dto.order !== undefined || dto.groupId !== undefined) {
                const card = prev?.cards.find(c => c._id === cardId);
                if (card) {
                    apply(moveCardLocally, {
                        cardId,
                        columnId: dto.columnId ?? card.columnId,
                        order: dto.order ?? card.order,
                        groupId: dto.groupId,
                    });
                }
            }
            if (dto.text !== undefined) {
                const card = prev?.cards.find(c => c._id === cardId);
                if (card) apply(upsertCard, { ...card, text: dto.text });
            }
            return { prev };
        },
        onError: (_e, _v, ctx) => restore(ctx?.prev),
        onSuccess: card => apply(upsertCard, card),
    });

    const deleteCard = useMutation({
        mutationFn: async (cardId: string) => api.delete(`${base(shareId)}/cards/${cardId}`),
        onMutate: cardId => {
            const prev = snapshot();
            apply(removeCard, { cardId });
            return { prev };
        },
        onError: (_e, _v, ctx) => restore(ctx?.prev),
    });

    const vote = useMutation({
        mutationFn: async ({ cardId, value }: { cardId: string; value: 1 | -1 | 0 }) =>
            (await api.post<RetroCardVotedEvent>(`${base(shareId)}/cards/${cardId}/vote`, { value })).data,
        onMutate: v => {
            const prev = snapshot();
            apply(voteLocally, v);
            return { prev };
        },
        onError: (_e, _v, ctx) => restore(ctx?.prev),
        onSuccess: result => apply(applyVote, result),
    });

    const comment = useMutation({
        mutationFn: async ({ cardId, text }: { cardId: string; text: string }) =>
            ({ cardId, comment: (await api.post<RetroCommentDto>(`${base(shareId)}/cards/${cardId}/comments`, { text })).data }),
        onSuccess: result => apply(addComment, result),
    });

    const addAction = useMutation({
        mutationFn: async (dto: CreateRetroActionItemDto) => (await api.post<RetroActionItemDto[]>(`${base(shareId)}/action-items`, dto)).data,
        onSuccess: items => apply(setActions, items),
    });

    const updateAction = useMutation({
        mutationFn: async ({ itemId, dto }: { itemId: string; dto: UpdateRetroActionItemDto }) =>
            (await api.put<RetroActionItemDto[]>(`${base(shareId)}/action-items/${itemId}`, dto)).data,
        onMutate: ({ itemId, dto }) => {
            const prev = snapshot();
            if (prev && dto.completed !== undefined) {
                apply(setActions, prev.retro.actionItems.map(i => (i.id === itemId ? { ...i, completed: dto.completed! } : i)));
            }
            return { prev };
        },
        onError: (_e, _v, ctx) => restore(ctx?.prev),
        onSuccess: items => apply(setActions, items),
    });

    const deleteAction = useMutation({
        mutationFn: async (itemId: string) => (await api.delete<RetroActionItemDto[]>(`${base(shareId)}/action-items/${itemId}`)).data,
        onSuccess: items => apply(setActions, items),
    });

    return { join, update, start, phase, conclude, archive, remove, timer, addCard, updateCard, deleteCard, vote, comment, addAction, updateAction, deleteAction };
}

export type RetroActions = ReturnType<typeof useRetroActions>;
